import type { AuthSessionManagerInterface } from '@app/packages/api/AuthSessionManager'
// The rule for a body that ended early is transport-level and shared with the api package.
import { parseResponseBody } from '@app/packages/api/response'
import { intoResultAsync } from '@app/packages/Result/Result'

import { RequestError } from './errors'
import { assertResponseHasNoError } from './response'
import type { InternalApiAction, InternalApiDispatcherDispatchOptions, InternalApiDispatcherInterface } from './utils'

type FetchType = typeof fetch

export type ApiDispatcherConstructorOptions = {
  base: string
  fetch: FetchType
  /** Required, unlike the rest: these carry the caller's locale, and a body localized on the server is wrong without it. */
  getHeaders: () => Record<string, string>
  authSession?: AuthSessionManagerInterface
}

export class ApiDispatcher implements InternalApiDispatcherInterface {
  init: ApiDispatcherConstructorOptions

  constructor(init: ApiDispatcherConstructorOptions) {
    this.init = init
  }

  dispatch = <RR>(
    apiAction: (dispatch: <R>(action: InternalApiAction) => Promise<R>, options?: InternalApiDispatcherDispatchOptions) => Promise<RR>,
    options?: InternalApiDispatcherDispatchOptions
  ): Promise<RR> => {
    return apiAction(action => this.dispatchAction(action, options), options)
  }

  private dispatchAction = async <R>(action: InternalApiAction, options?: InternalApiDispatcherDispatchOptions): Promise<R> => {
    let headers: Record<string, string> = { ...this.init.getHeaders() }
    if (!action.body || typeof action.body === 'string') {
      headers['content-type'] = 'application/json'
    }
    if (this.init.authSession && !action.skipAuth) {
      const token = await this.init.authSession.ensureAccessToken({ signal: options?.abortSignal })
      if (token !== null) {
        headers['Authorization'] = `Bearer ${token}`
      }
    }
    if (action.headers) {
      headers = action.headers(headers)
    }

    const respResult = await intoResultAsync(
      this.init.fetch(`${this.init.base}${action.endpoint}`, {
        method: action.method,
        headers,
        body: action.body,
        credentials: 'same-origin',
        redirect: 'follow',
        signal: options?.abortSignal,
      })
    )

    if (respResult.error) {
      throw new RequestError(respResult.value instanceof Error ? respResult.value.message : String(respResult.value), { cause: respResult.value })
    }

    if (!respResult.value.ok) {
      await assertResponseHasNoError(respResult.value, action.method, `${this.init.base}${action.endpoint}`)
    }

    const text = await respResult.value.text()
    if (!text) return undefined as R
    return parseResponseBody(text, `${action.method} ${action.endpoint}`) as R
  }
}
